import { useState, useEffect, useRef } from 'react';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import config from '../../config';
import { motion } from 'framer-motion'
import ProfileMenu from '../../components/ProfileMenu';
import VerticalSelector from '../../components/VerticalSelector';
import { isInWebView, getCookie, isEmoji } from '../utils';

// Global Definition
const DEFAULT_AVATAR_URL = 'https://heyingdu.com/heying.png';
const workoutTypes = [
  { name: 'Weight Lifting', icon: '🏋️', color: '#FECA57' },
  { name: 'Running', icon: '🏃', color: '#FF6B6B' },
  { name: 'HIIT', icon: '🔥', color: '#FF1493' },
  { name: 'Cycling', icon: '🚴', color: '#4ECDC4' },
  { name: 'Swimming', icon: '🏊', color: '#45B7D1' },
  { name: 'Yoga', icon: '🧘', color: '#96CEB4' },
  { name: 'Frisbee', icon: '🥏', color: '#FFB6C1' },
  { name: 'Soccer', icon: '⚽', color: '#FF6347' },
  { name: 'Badminton', icon: '🏸', color: '#FF8C00' },
  { name: 'Table Tennis', icon: '🏓', color: '#FF4500' },
  { name: 'Rugby', icon: '🏉', color: '#FF6F61' },
  { name: 'Pilates', icon: '🧘‍♀️', color: '#FFD700' },
  { name: 'Basketball', icon: '⛹️', color: '#FF9FF3' },
  { name: 'Tennis', icon: '🎾', color: '#54A0FF' },
  { name: 'Boxing', icon: '🥊', color: '#FF6348' },
  { name: 'Dancing', icon: '💃', color: '#DDA0DD' },
  { name: 'Walking', icon: '🚶', color: '#98D8C8' },
  { name: 'Hiking', icon: '🥾', color: '#8FBC8F' },
  { name: 'Pilates', icon: '🧘‍♀️', color: '#FFD700' },
  { name: 'Rowing', icon: '🚣', color: '#FF4500' },
  { name: 'Climbing', icon: '🧗', color: '#4682B4' },
  { name: 'CrossFit', icon: '🏋️‍♂️', color: '#FF8C00' },
  { name: 'Martial Arts', icon: '🥋', color: '#20B2AA' },
  { name: 'Pilates', icon: '🧘‍♀️', color: '#FFD700' },
  { name: 'Baseball', icon: '⚾', color: '#FF6347' },
  { name: 'Football', icon: '🏈', color: '#32CD32' },
  { name: 'Golf', icon: '⛳', color: '#8B4513' },
  { name: 'Surfing', icon: '🏄', color: '#1E90FF' },
  { name: 'Skiing', icon: '🏂', color: '#FF69B4' },
  { name: 'Skating', icon: '⛸️', color: '#7B68EE' },
  { name: 'Volleyball', icon: '🏐', color: '#FF4500' },
];
const extendedWorkoutTypes = [
  ...workoutTypes,
  {
    icon: "",
    name: "Other",
    color: "#999999"
  }
];



// Date Formatting Helpers for calendar
const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isToday = (date) => isSameDay(date, new Date());



const Fit = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [workouts, setWorkouts] = useState({});
  const [muscleGroup, setMuscleGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedWorkoutType, setSelectedWorkoutType] = useState(0);
  const [duration, setDuration] = useState('30');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginMode, setLoginMode] = useState('login');
  const [longPressTarget, setLongPressTarget] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDuration, setEditDuration] = useState('');
  const [editMuscleGroup, setEditMuscleGroup] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [, setIsLoggingOut] = useState(false);
  const [user, setUser] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [otherIcon, setOtherIcon] = useState('');
  const [otherName, setOtherName] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  useEffect(() => {
    async function init() {
      await fetch(`${config.apiBaseUrl}/api/get-csrf/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });
      const savedUser = localStorage.getItem('authUser');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsLoggedIn(true);
      }
      try {
        const res = await fetch(`${config.apiBaseUrl}/api/current-user/`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
          },
        });

        if (res.ok) {
          const data = await res.json();
          setUser({
            id: data.id,
            name: data.username,
            email: data.email,
            avatarUrl: data.avatarUrl || DEFAULT_AVATAR_URL,
          });
          setIsLoggedIn(true);
          localStorage.setItem('authUser', JSON.stringify(data));
        } else {
          setIsLoggedIn(false);
          localStorage.removeItem('authUser');
        }
      } catch (err) {
        console.error('Unable to check login:', err);
      } finally {
        setLoading(false);
      }
    }

    init();
  }, []);

  // Login related functions
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userStr = params.get('user');

    if (userStr) {
      try {
        const decoded = JSON.parse(atob(userStr));
        setUser({
          id: decoded.id,
          name: decoded.username,
          email: decoded.email,
          avatarUrl: decoded.avatarUrl || DEFAULT_AVATAR_URL,
        });
        setIsLoggedIn(true);
        localStorage.setItem('authUser', JSON.stringify(decoded));

        // 清除 URL 参数
        params.delete('user');
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
      } catch (err) {
        console.error('解析登录用户失败', err);
      }
    }
  }, []);
  async function handleLoginOrRegister() {
    try {
      const url = loginMode === 'login'
        ? `${config.apiBaseUrl}/api/login/`
        : `${config.apiBaseUrl}/api/register/`;

      const payload =
        loginMode === 'login'
          ? { email, password }
          : { email, password, confirmPassword };

      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        alert('Error: ' + (errorData.detail || '请求失败'));
        return;
      }

      const data = await res.json();
      console.log('成功:', data);

      setUser({
        id: data.user.id,
        name: data.user.username,
        email: data.user.email,
        avatarUrl: data.user.avatarUrl || DEFAULT_AVATAR_URL,
      });
      setIsLoggedIn(true);
      setShowLoginModal(false);

    } catch (error) {
      console.error('网络错误', error);
      alert('网络错误，请稍后重试');
    }
  }

  async function handleAddWorkout() {
    if (!isLoggedIn) {
      alert('Login Required');
      return;
    }

    const selected = extendedWorkoutTypes[selectedWorkoutType];
    const isOther = selected.name === 'Other';

    // 校验 Other 情况下的输入
    if (isOther) {
      if (!isEmoji(otherIcon)) {
        alert("Icon Invalid");
        return;
      }
      if (!otherName.trim()) {
        alert("Name Invalid");
        return;
      }
    }

    // 确定实际使用的图标、名称和颜色
    const actualName = isOther ? otherName.trim() : selected.name;
    const actualIcon = isOther ? otherIcon : selected.icon;
    const actualColor = isOther ? '#999999' : selected.color;

    const newWorkoutData = {
      date: formatDateKey(selectedDate), // '2025-06-17' 这种
      workout_type: actualName, // 实际名称
      duration: parseInt(duration),
      timestamp: new Date().toISOString(),
      weightlift_part: actualName === 'Weight Lifting' ? muscleGroup : '',
      icon: actualIcon,
    };

    try {
      const res = await fetch(`${config.apiBaseUrl}/api/workouts/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        body: JSON.stringify(newWorkoutData),
      });

      if (!res.ok) throw new Error('Failed to create workout');

      const created = await res.json();

      const dateKey = created.date;

      // 如果前端能匹配到内置类型，就用内置；否则 fallback 到自定义图标/颜色
      const matched = extendedWorkoutTypes.find(t => t.name === created.workout_type);
      const newWorkout = {
        id: created.id,
        type: matched || {
          name: actualName,
          icon: actualIcon,
          color: actualColor,
        },
        duration: created.duration,
        timestamp: new Date(created.timestamp).getTime(),
        weightlift_part: created.weightlift_part || '',
        last_modified_by: {
          id: user.id,
          username: user.name,
          avatar_url: user.avatarUrl || DEFAULT_AVATAR_URL,
        },
      };

      setWorkouts(prev => {
        const old = prev[dateKey] || [];
        const updated = { ...prev, [dateKey]: [...old, newWorkout] };
        localStorage.setItem('cachedWorkouts', JSON.stringify(updated));
        return updated;
      });

      // 清空表单
      setShowAddModal(false);
      setDuration('30');
      setMuscleGroup('');
      setOtherName('');
      setOtherIcon('');

    } catch (err) {
      console.error('Failed to add workout:', err);
      alert('Failed to add workout. Please try again.');
    }
  }



  const handleLogout = async () => {
    setIsLoggingOut(true);
    localStorage.removeItem('authUser');
    setUser(null);
    setIsLoggedIn(false);

    try {
      await fetch(`${config.apiBaseUrl}/api/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
    } catch (error) {
      console.error('登出失败（后端接口未响应）:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const longPressTimer = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutside =
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target);

      if (clickedOutside) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    const cacheKey = 'cachedWorkouts';
    let pollingInterval = null;

    const fetchData = async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/api/workouts/`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
          },
        });
        const data = await res.json();
        console.log('Fetched workouts:', data);
        const grouped = {};

        data.forEach(item => {
          // ✅ 筛选逻辑：只看自己
          if (
            showOnlyMine &&
            item.last_modified_by?.id !== user?.id
          ) {
            return;
          }

          const key = item.date;
          if (!grouped[key]) grouped[key] = [];
          const matched = workoutTypes.find(t => t.name === item.workout_type);
          grouped[key].push({
            id: item.id,
            type: {
              name: item.workout_type,
              icon: item.icon || matched?.icon || '❓',
              color: matched?.color || '#ccc',
            },
            duration: item.duration,
            timestamp: new Date(item.timestamp).getTime(),
            weightlift_part: item.weightlift_part || '',
            last_modified_by: item.last_modified_by || null,
          });
        });

        // ✅ 无论数据是否变动，只要 showOnlyMine 改变，就更新 state
        localStorage.setItem(cacheKey, JSON.stringify(data)); // 缓存原始 data
        setWorkouts(grouped);
      } catch (err) {
        console.error('Fetch workouts failed:', err);
      }
    };

    const init = () => {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          const grouped = {};

          data.forEach(item => {
            if (
              showOnlyMine &&
              item.last_modified_by?.id !== user?.id
            ) {
              return;
            }

            const key = item.date;
            if (!grouped[key]) grouped[key] = [];
            const matched = workoutTypes.find(t => t.name === item.workout_type);
            grouped[key].push({
              id: item.id,
              type: {
                name: item.workout_type,
                icon: item.icon || matched?.icon || '❓',
                color: matched?.color || '#ccc',
              },
              duration: item.duration,
              timestamp: new Date(item.timestamp).getTime(),
              weightlift_part: item.weightlift_part || '',
              last_modified_by: item.last_modified_by || null,
            });
          });

          setWorkouts(grouped);
          setLoading(false);
        } catch { }
      }

      fetchData();
      pollingInterval = setInterval(fetchData, 5000);
    };

    init();

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [showOnlyMine, user?.id]);

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const renderCalendar = () => {
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(<div key={`e-${i}`} className="aspect-square" />);
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateKey = formatDateKey(date);
      const dayWorkouts = workouts[dateKey] || [];
      const isSelected = isSameDay(date, selectedDate);
      const isTodayDate = isToday(date);
      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`h-12 border rounded-lg flex flex-col items-center justify-start p-1 cursor-pointer transition-all ${isTodayDate
              ? 'bg-blue-500 text-white border-blue-500'
              : isSelected
                ? 'border-blue-500 bg-white text-black'
                : 'bg-white text-black hover:bg-gray-50 hover:border-gray-200'
            }`}
        >
          <span
            className={`text-xs font-semibold mb-1 ${isTodayDate ? 'text-white' : 'text-gray-800'
              }`}
          >
            {day}
          </span>
          {dayWorkouts.length > 0 && (
            <div className="flex flex-wrap gap-0.5 items-start justify-center max-w-full">
              {dayWorkouts
                .slice(0, window.innerWidth < 640 ? 3 : 4)
                .map((w, i) => (
                  <span
                    key={i}
                    className="text-xs leading-none"
                    style={{ fontSize: window.innerWidth < 640 ? '8px' : '10px' }}
                  >
                    {w.type.icon}
                  </span>
                ))}
              {dayWorkouts.length > (window.innerWidth < 640 ? 3 : 4) && (
                <span
                  className={`text-xs leading-none ${isSelected || isTodayDate ? 'text-white' : 'text-gray-400'
                    }`}
                >
                  +{dayWorkouts.length - (window.innerWidth < 640 ? 3 : 4)}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }
    return days;
  };

  const handleLongPressStart = (workout, index) => {
    setActiveIndex(index);
    longPressTimer.current = setTimeout(() => {
      setLongPressTarget({ workout, index });
      setEditDuration(workout.duration.toString());
      setEditMuscleGroup(workout.weightlift_part || '');
      setShowEditModal(true);
    }, 700);
  };
  const handleLongPressEnd = () => {
    setActiveIndex(null);
    clearTimeout(longPressTimer.current);
  };
  const handleDelete = () => {
    if (!isLoggedIn) {
      alert('请先登录才能删除记录');
      return;
    }

    if (!longPressTarget) return;
    const workoutId = longPressTarget.workout.id;
    if (!workoutId) {
      console.error('No workout id for deletion');
      return;
    }

    fetch(`${config.apiBaseUrl}/api/workouts/${workoutId}/`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
      },
    })
      .then(res => {
        if (!res.ok) throw new Error('Delete failed');
        const dateKey = formatDateKey(selectedDate);
        setWorkouts(prev => {
          const newWorkouts = [...(prev[dateKey] || [])];
          newWorkouts.splice(longPressTarget.index, 1);
          const updated = { ...prev, [dateKey]: newWorkouts };
          localStorage.setItem('cachedWorkouts', JSON.stringify(updated));
          return updated;
        });
        setShowEditModal(false);
        setLongPressTarget(null);
      })
      .catch(err => {
        console.error('Failed to delete workout:', err);
      });
  };
  const handleEditSave = () => {
    if (!isLoggedIn) {
      alert('Login required');
      return;
    }

    if (!longPressTarget) return;
    const workoutId = longPressTarget.workout.id;
    if (!workoutId) {
      console.error('No workout id for update');
      return;
    }

    const updatedData = {
      duration: parseInt(editDuration),
    };
    if (longPressTarget.workout.type.name === 'Weight Lifting') {
      updatedData.weightlift_part = editMuscleGroup;
    }

    fetch(`${config.apiBaseUrl}/api/workouts/${workoutId}/`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCookie('csrftoken'),
      },
      body: JSON.stringify(updatedData),
    })
      .then(res => {
        if (!res.ok) throw new Error('Update failed');
        return res.json();
      })
      .then(updatedWorkout => {
        const dateKey = formatDateKey(selectedDate);
        setWorkouts(prev => {
          const newWorkouts = [...(prev[dateKey] || [])];
          newWorkouts[longPressTarget.index] = {
            ...newWorkouts[longPressTarget.index],
            ...updatedWorkout,
            type: newWorkouts[longPressTarget.index].type,
          };

          const updated = { ...prev, [dateKey]: newWorkouts };
          localStorage.setItem('cachedWorkouts', JSON.stringify(updated));
          return updated;
        });

        setShowEditModal(false);
        setLongPressTarget(null);
      })
      .catch(err => {
        console.error('Failed to update workout:', err);
      });
  };

  const selectedDateWorkouts = workouts[formatDateKey(selectedDate)] || [];

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50 pb-4">
      <div className="bg-white shadow-sm px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() =>
              setCurrentDate(
                new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
              )
            }
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <h1 className="text-xl font-semibold text-gray-800">
            {currentDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })}
          </h1>

          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setCurrentDate(
                  new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
                )
              }
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>

            <div className="relative">
              {isLoggedIn && user ? (
                <>
                  {/* 点击头像：切换菜单显示 */}
                  <div
                    className="flex items-center space-x-2 cursor-pointer"
                    title="Click to toggle menu"
                    ref={buttonRef}
                    onClick={() => {
                      setShowProfileMenu(prev => !prev);
                      // const menu = menuRef.current;
                      // if (menu) {
                      //   menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                      // }
                    }}
                  >
                    <img
                      src={user.avatarUrl || DEFAULT_AVATAR_URL}
                      alt="User Avatar"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="hidden sm:inline text-gray-700">{user.name}</span>
                  </div>
                  {showProfileMenu && (
                    <ProfileMenu ref={menuRef} user={user} onClose={() => setShowProfileMenu(false)} showOnlyMine={showOnlyMine}
                      setShowOnlyMine={setShowOnlyMine} handleLogout={handleLogout} />
                  )}
                  {/* 菜单项：使用 ref 控制显示 */}
                  {/* <div
        ref={menuRef}
        className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none' }}
      >
        <button
          className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          onClick={() => {
            
            setShowOnlyMine(prev => !prev);
            if (menuRef.current) menuRef.current.style.display = 'none';
          }}
        >
          {showOnlyMine ? '👥' : '👤'}
        </button>
        <button
          className="block w-full text-left px-4 py-2 hover:bg-gray-100 border-t"
          onClick={() => {
            if (menuRef.current) menuRef.current.style.display = 'none';
            handleLogout();
          }}
        >
          Logout
        </button>
      </div> */}
                </>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="rounded-md bg-blue-600 px-4 py-1 text-white hover:bg-blue-700 transition"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="h-6 flex items-center justify-center">
              <span className="text-xs font-medium text-gray-500">{d}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1" style={{ gridAutoRows: '3rem' }} >{renderCalendar()}</div>
      </div>
      <div className="flex-1 overflow-hidden px-4 py-2 h-0">
        <div className="bg-white rounded-xl p-4 shadow-sm flex flex-col h-full">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">
              {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-lg"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-1 max-h-[400px]">  {/* 可上下滑动区域（当内容超出时） */}
            {selectedDateWorkouts.length === 0 ? (
              <p className="text-gray-500 text-center py-8 text-base">
                No workouts recorded
              </p>
            ) : (
              <div className="space-y-3">
                {selectedDateWorkouts.map((workout, index) => (
                  <motion.div
                    id={`workout-${index}`}
                    key={index}
                    onMouseDown={() => handleLongPressStart(workout, index)}
                    onTouchStart={() => handleLongPressStart(workout, index)}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={handleLongPressEnd}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressEnd}
                    initial={false}
                    animate={activeIndex === index ? { scale: 1.02 } : { scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                    }}
                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg select-none shadow-md"
                  >
                    {/* 左侧图标 */}
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                      style={{ backgroundColor: workout.type.color + '20' }}
                    >
                      {workout.type.icon}
                    </div>

                    {/* 中间内容，flex-1 占满剩余空间 */}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800 text-base">
                        {workout.type.name}
                      </h3>

                      {/* 时间和weightlift_part横向排列 */}
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>{workout.duration} minutes</span>
                        {workout.type.name === 'Weight Lifting' && workout.weightlift_part && (
                          <span className="font-semibold text-blue-600 bg-blue-100 rounded px-1 select-none">
                            {workout.weightlift_part}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 右侧头像 */}
                    <div className="w-8 h-8 rounded-full border border-gray-300 select-none overflow-hidden bg-gray-100 flex items-center justify-center">
                      {workout.last_modified_by?.avatar_url ? (
                        <img
                          src={workout.last_modified_by.avatar_url}
                          alt={`${workout.last_modified_by.username || 'User'} avatar`}
                          className="rounded-full w-8 h-8 object-cover"
                        />
                      ) : (
                        // 空内容，留白显示空圆圈
                        null
                      )}
                    </div>

                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新增弹窗 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div
            className="bg-white w-full rounded-t-3xl p-6 max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Add Workout</h2>
            <VerticalSelector
              options={extendedWorkoutTypes}
              selectedIndex={selectedWorkoutType}
              setSelectedIndex={setSelectedWorkoutType}
              labelKey="name"
              iconKey="icon"
            />;
            <div className="mt-4">
              <label className="block mb-2 font-medium">Duration (minutes)</label>
              <input
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full border border-gray-300 rounded p-2"
              />
            </div>
            {workoutTypes[selectedWorkoutType]?.name === 'Weight Lifting' && (
              <div className="mt-4">
                <label className="block mb-2 font-medium">Target Muscle Group</label>
                <input
                  type="text"
                  value={muscleGroup}
                  onChange={(e) => setMuscleGroup(e.target.value)}
                  placeholder="e.g. Chest, Legs"
                  className="w-full border border-gray-300 rounded p-2"
                />
              </div>
            )}
            {extendedWorkoutTypes[selectedWorkoutType]?.name === 'Other' && (
              <div className="mt-4">
                <label className="block mb-2 font-medium">Custom Workout Type</label>
                <div className="flex space-x-2">
                  {/* Icon */}
                  <input
                    type="text"
                    value={otherIcon}
                    onChange={(e) => setOtherIcon(e.target.value)}
                    placeholder="e.g. 🧘"
                    maxLength={2}
                    className="w-1/4 border border-gray-300 rounded p-2 text-center"
                  />

                  {/* Name */}
                  <input
                    type="text"
                    value={otherName}
                    onChange={(e) => setOtherName(e.target.value)}
                    placeholder="e.g. Stretch"
                    maxLength={10}
                    className="w-3/4 border border-gray-300 rounded p-2"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 rounded border border-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddWorkout}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 编辑运动弹窗 */}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowEditModal(false);
            setLongPressTarget(null);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-md space-y-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Edit Workout</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              min={1}
              value={editDuration}
              onChange={(e) => setEditDuration(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
            />

            {longPressTarget?.workout.type.name === 'Weight Lifting' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Muscle Group
                </label>
                <input
                  type="text"
                  value={editMuscleGroup}
                  onChange={(e) => setEditMuscleGroup(e.target.value)}
                  placeholder="e.g. Chest, Legs"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </>
            )}

            <div className="flex justify-between mt-6">
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </button>
              <div className="space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setLongPressTarget(null);
                  }}
                  className="px-4 py-2 rounded border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* {showEditModal && longPressTarget && (
  <div
    className="absolute z-40 bg-white border border-gray-300 rounded-lg p-4 shadow-xl"
    style={{
      top: longPressTarget.position?.top || 100,
      left: longPressTarget.position?.left || 100,
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <h3 className="text-lg font-semibold text-gray-800 mb-2">Edit Workout</h3>

    <label className="block text-sm font-medium text-gray-700 mb-1">
      Duration (minutes)
    </label>
    <input
      type="number"
      min={1}
      value={editDuration}
      onChange={(e) => setEditDuration(e.target.value)}
      className="w-full p-2 border border-gray-300 rounded mb-4"
    />

    {longPressTarget?.workout.type.name === 'Weight Lifting' && (
      <>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Target Muscle Group
        </label>
        <input
          type="text"
          value={editMuscleGroup}
          onChange={(e) => setEditMuscleGroup(e.target.value)}
          placeholder="e.g. Chest, Legs"
          className="w-full p-2 border border-gray-300 rounded"
        />
      </>
    )}

    <div className="flex justify-between mt-4">
      <button
        onClick={handleDelete}
        className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm"
      >
        Delete
      </button>
      <div className="space-x-2">
        <button
          onClick={() => {
            setShowEditModal(false);
            setLongPressTarget(null);
          }}
          className="px-3 py-1 rounded border border-gray-300 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleEditSave}
          className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
        >
          Save
        </button>
      </div>
    </div>
  </div>
)} */}
      {/* 登录注册弹窗 */}
      {showLoginModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => setShowLoginModal(false)}
        >
          <div
            className="bg-white rounded-lg p-6 w-full max-w-sm shadow-lg animate-zoomIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Tabs */}
            <div className="flex justify-center mb-6">
              <button
                onClick={() => setLoginMode('login')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${loginMode === 'login'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500'
                  }`}
              >
                Login
              </button>
              <button
                onClick={() => setLoginMode('register')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ml-4 ${loginMode === 'register'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500'
                  }`}
              >
                Register
              </button>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                />
              </div>

              {/* 注册模式下显示确认密码 */}
              {loginMode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
              )}

              {/* 登录/注册按钮 */}
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="px-4 py-2 border rounded text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLoginOrRegister}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {loginMode === 'login' ? 'Login' : 'Register'}
                </button>
              </div>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">or</span>
                </div>
              </div>

              {/* Google 登录按钮 */}
              <button
                onClick={() => {
                  const loginUrl = `${config.apiBaseUrl}/api/google-login/`;

                  if (isInWebView()) {
                    const confirmExternal = window.confirm('Login with Google in external browser?');
                    if (confirmExternal) {
                      // 打开外部浏览器
                      window.open(loginUrl, '_blank');
                    }
                  } else {
                    // 正常跳转
                    window.location.href = loginUrl;
                  }
                }}
                className="w-full flex items-center justify-center border border-gray-300 rounded px-4 py-2 text-sm font-medium hover:bg-gray-100"
              >
                <img
                  src="https://www.svgrepo.com/show/475656/google-color.svg"
                  alt="Google"
                  className="w-5 h-5 mr-2"
                />
                Continue with Google
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Fit;